import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
    PieChart,
    Vote,
    CheckCircle2,
    Clock,
    Users,
    BarChart3
} from 'lucide-react';
import { cn } from '../lib/utils';

const Polls: React.FC = () => {
    const [polls, setPolls] = useState<any[]>([]);
    const [pollResults, setPollResults] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPolls();
    }, []);

    const fetchPolls = async () => {
        const res = await api.get('/polls');
        setPolls(res.data);
        setLoading(false);
        res.data.forEach((p: any) => fetchResults(p.id));
    };

    const fetchResults = async (pollId: string) => {
        try {
            const res = await api.get(`/polls/${pollId}/results`);
            setPollResults(prev => ({ ...prev, [pollId]: res.data }));
        } catch (e) { console.error(e); }
    };

    const handleVote = async (pollId: string, optionId: string) => {
        try {
            await api.post('/polls/vote', { poll_id: pollId, option_id: optionId });
            alert('Your vote has been recorded.');
            fetchResults(pollId);
        } catch (e) {
            alert('You have already voted in this poll.');
        }
    };

    return (
        <div className="space-y-8 max-w-4xl mx-auto pb-10">
            <div>
                <h1 className="text-3xl font-bold text-slate-900">Decision Polls</h1>
                <p className="text-slate-500 mt-1">Have your say in society matters</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {polls.map((p: any) => {
                    const isExpired = p.expires_at && new Date(p.expires_at) < new Date();
                    const results = pollResults[p.id];
                    const hasVoted = results?.user_has_voted;

                    return (
                        <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col hover:border-violet-300 transition-all group">
                            <div className="p-6 flex-1">
                                <div className="flex justify-between items-start mb-4">
                                    <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center text-violet-600">
                                        <BarChart3 size={20} />
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className={cn(
                                            "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border",
                                            isExpired ? "bg-slate-50 text-slate-400 border-slate-100" : "bg-emerald-50 text-emerald-700 border-emerald-100"
                                        )}>
                                            {isExpired ? 'Closed' : 'Active'}
                                        </span>
                                        {p.expires_at && (
                                            <p className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                                <Clock size={10} /> {new Date(p.expires_at).toLocaleDateString()}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <h2 className="text-lg font-bold text-slate-900 mb-2">{p.question}</h2>
                                {p.description && <p className="text-xs text-slate-500 mb-6">{p.description}</p>}

                                <div className="space-y-3">
                                    {hasVoted || isExpired ? (
                                        <div className="space-y-4 py-2">
                                            {results?.results.map((r: any) => (
                                                <div key={r.option_id} className="space-y-1">
                                                    <div className="flex justify-between text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                                        <span className="flex items-center gap-2">
                                                            {r.option_text}
                                                            {results.user_voted_option === r.option_id && <CheckCircle2 size={10} className="text-emerald-500" />}
                                                        </span>
                                                        <span>{r.percentage}%</span>
                                                    </div>
                                                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                                        <div
                                                            className={cn("h-full rounded-full transition-all duration-1000", results.user_voted_option === r.option_id ? "bg-violet-600" : "bg-slate-300")}
                                                            style={{ width: `${r.percentage}%` }}
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        p.options.map((opt: any) => (
                                            <button
                                                key={opt.id}
                                                onClick={() => handleVote(p.id, opt.id)}
                                                className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-200 text-sm font-bold text-slate-700 hover:border-violet-500 hover:bg-violet-50 transition-all group/opt"
                                            >
                                                <span>{opt.option_text}</span>
                                                <Vote size={16} className="text-violet-500 opacity-0 group-hover/opt:opacity-100 transition-opacity" />
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="bg-slate-50 px-6 py-3 flex items-center justify-between border-t border-slate-100">
                                <div className="flex items-center gap-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    <Users size={14} /> {results?.total_votes || 0} Votes
                                </div>
                                <div className="text-[10px] font-bold text-violet-600 uppercase tracking-widest">
                                    {p.is_anonymous ? 'Anonymous' : 'Public'}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Polls;
